import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useBi, useTranslation } from '@wix/yoshi-flow-editor';

import { groupsSearchSearchResultSrc111Evid523 } from '@wix/bi-logger-groups-data/v2';

import { selectGroupName, selectSearchMetadata } from 'store/selectors';
import { useController } from 'common/context/controller';

import { Box } from 'wui/Box';
import { Spinner } from 'wui/Spinner';
import { ErrorState } from 'wui/ErrorState';
import { EmptyState } from 'wui/EmptyState';

import { FeedResultsList } from './FeedResultsList';

interface IProps {
  groupId: string;
  query?: string;
  container: HTMLElement | null;

  onReset(): void;

  origin: string;
}

export function SearchResults(props: IProps) {
  const { groupId, query, container, onReset, origin } = props;

  const bi = useBi();
  const { t } = useTranslation();
  const { search$ } = useController();

  const groupName = useSelector(selectGroupName(groupId));
  const { isLoading, isError } = useSelector(selectSearchMetadata);

  useEffect(() => {
    if (query) {
      search$.query({
        query,
        groupId,
      });
    }
  }, [groupId, query]);

  if (!query) {
    return (
      <EmptyState
        bw
        variant="section"
        title={t('groups-web.group-search.empty-state.title')}
        subtitle={t('groups-web.group-search.empty-state.subtitle', {
          groupName,
        })}
      />
    );
  }

  if (isLoading) {
    return (
      <Box
        height="100%"
        align="center"
        direction="vertical"
        verticalAlign="middle"
      >
        <Spinner />
      </Box>
    );
  }

  if (isError) {
    bi.report(
      groupsSearchSearchResultSrc111Evid523({
        origin,
        groupId,
        inputText: query,
        responseStatus: 'error',
        tabName: 'posts',
      }),
    );

    return (
      <ErrorState
        bw
        variant="section"
        onRetry={handleRetry}
        buttonProps={{ variant: 'basic', outlined: true }}
        title={t('groups-web.group-search.error-state.title')}
        subtitle={t('groups-web.group-search.error-state.subtitle')}
        actionLabel={t('groups-web.group-search.error-state.retry')}
      />
    );
  }

  return (
    <FeedResultsList
      query={query}
      groupId={groupId}
      origin={origin}
      onReset={onReset}
      container={container}
    />
  );

  function handleRetry() {
    search$.query({
      groupId,
      query: query!,
    });
  }
}

SearchResults.displayName = 'SearchResults';
